import { TextField } from "@material-ui/core";
import { Autocomplete as MuiAutocomplete } from "@material-ui/lab";
import { compose } from "ramda";
import { memo } from "react";
import { Control, Controller } from "react-hook-form";
import { useSelector } from "react-redux";

import { RootState } from "../../store";
import { decodeComponentId } from "../../utilities/forms";
import { withError } from "../HOC";
import { CommonFormElProps } from "../types";

export type AutocompleteItem = {
  readonly label: string;
  readonly value: string;
};

export type AutocompleteProps = {
  readonly control: Control;
  readonly items:
    | Array<AutocompleteItem>
    | ((store: RootState) => Array<AutocompleteItem>);
  readonly defaultValue?: AutocompleteItem;
} & Omit<CommonFormElProps, "defaultValue">;

export const Autocomplete = memo<
  AutocompleteProps & Required<Pick<CommonFormElProps, "onChange">>
>(
  ({
    control,
    defaultValue,
    hasError,
    id,
    items: selectItems,
    label,
    onChange: onChangeAction,
    required = false,
  }) => {
    const items = useSelector((state: RootState) => {
      if (Array.isArray(selectItems)) {
        return selectItems;
      }

      return selectItems(state);
    });

    return (
      <Controller
        render={({ onChange, ...props }) => (
          <MuiAutocomplete
            blurOnSelect
            disableClearable
            getOptionLabel={(option: AutocompleteItem) =>
              option.label || defaultValue?.label || ""
            }
            getOptionSelected={(
              option: AutocompleteItem,
              value: AutocompleteItem
            ) => option.value === value.value}
            id={id}
            onChange={(e, data) => {
              onChange(data);
              onChangeAction({
                field: id,
                value: data.value,
              });
            }}
            options={items}
            renderInput={(params) => (
              <TextField
                {...params}
                autoComplete="off"
                error={hasError}
                InputProps={{
                  ...params.InputProps,
                  notched: false,
                }}
                InputLabelProps={{ shrink: true, required }}
                label={label}
                variant="outlined"
              />
            )}
            {...props}
          />
        )}
        control={control}
        defaultValue={defaultValue}
        name={id}
        onChange={([_, data]: [unknown, string]) => data}
      />
    );
  }
);

export const AutocompleteWithError = compose(withError)(Autocomplete);
